import { Controller } from "@hotwired/stimulus"
import { formatMoney, centsToDecimal } from "../support/format_money"

/**
 * Base wizard controller — shared step navigation, validation, and review.
 *
 * Manages:
 *   - Step show/hide and indicator updates
 *   - Step validation framework (guest, dives, review)
 *   - Review card and item generation
 *   - Primary diver sync to dives controller
 *   - Form submission
 *
 * POS-specific features NOT included here (added by pos_wizard_controller):
 *   - PMS guest selection (selectPmsGuest, room tracking, reservation number)
 *   - Payment surcharge sync / room charge visibility
 *   - Agent attribution
 *   - Save draft
 *
 * Values:
 *   currentStep (Number)   — active step index (default 0)
 *   totalSteps (Number)    — number of steps (default 5)
 *   posCreateUrl (String)  — form action URL (subclass may use)
 *   divesIdentifier (String) — Stimulus identifier for the dives controller (default: auto-detected)
 */
export default class extends Controller {
  static targets = [
    "step",
    "stepIndicator",
    "stepConnector",
    "stepLabel",
    "currentStepLabel",
    "currentStepNumber",
    "diverIdField",
    "diverNameDisplay",
    "submitButton",
    "form",
    "reviewCards",
    "reviewTotal",
    "reviewItems",
    "paymentTotal"
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    minStep: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 5 },
    posCreateUrl: String
  }

  static PACKAGE_ICON = '<svg class="w-3.5 h-3.5 text-blue-400 dark:text-blue-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>'
  static ADDON_ICON = '<svg class="w-3.5 h-3.5 text-green-400 dark:text-green-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/></svg>'
  static ADDON_CATEGORIES = ["equipment", "additional_offering"]

  connect() {
    this.showStep(this.currentStepValue)
  }

  /**
   * The Stimulus identifier for the dives controller embedded in this wizard.
   * Override in subclass if using a different identifier.
   */
  get divesControllerIdentifier() {
    return this._detectDivesIdentifier()
  }

  // ===== Step Navigation =====

  nextStep(event) {
    event.preventDefault()

    if (!this.validateCurrentStep()) return

    const nextIndex = Math.min(this.currentStepValue + 1, this.totalStepsValue - 1)

    // Transition hooks — subclass can override
    this.onStepTransition(this.currentStepValue, nextIndex)

    this.showStep(nextIndex)
  }

  previousStep(event) {
    event.preventDefault()
    const prevIndex = Math.max(this.currentStepValue - 1, this.minStepValue)
    this.showStep(prevIndex)
  }

  goToStep(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.step)
    if (index < this.minStepValue) return
    if (index <= this.currentStepValue) {
      if (index === 2) this.buildReview()
      if (index === 3) this.onEnterPaymentStep()
      this.showStep(index)
    }
  }

  showStep(index) {
    this.currentStepValue = index

    this.stepTargets.forEach((step, i) => {
      step.classList.toggle("hidden", i !== index)
    })

    this.element.scrollIntoView({ behavior: "smooth", block: "start" })

    this.stepIndicatorTargets.forEach((indicator, i) => {
      // Strip every state-dependent class — including the initial pre-JS
      // styling (bg-white / border-2 / border-gray-300) so it doesn't bleed
      // through after JS-applied classes. Dark peers are removed alongside
      // their light counterparts to keep both modes consistent.
      indicator.classList.remove(
        "bg-blue-600", "bg-green-500", "bg-gray-200", "dark:bg-base-700", "bg-white", "dark:bg-base-800",
        "text-white", "text-gray-500", "dark:text-base-400",
        "border-2", "border-gray-300", "dark:border-base-600",
        "active", "completed"
      )

      const check = indicator.querySelector("[data-step-check]")
      const number = indicator.querySelector("[data-step-number]")

      if (i < index) {
        indicator.classList.add("bg-green-500", "text-white", "completed")
        check?.classList.remove("hidden")
        number?.classList.add("hidden")
      } else if (i === index) {
        indicator.classList.add("bg-blue-600", "text-white", "active")
        check?.classList.add("hidden")
        number?.classList.remove("hidden")
      } else {
        indicator.classList.add("bg-white", "dark:bg-base-800", "border-2", "border-gray-300", "dark:border-base-600", "text-gray-500", "dark:text-base-400")
        check?.classList.add("hidden")
        number?.classList.remove("hidden")
      }
    })

    // Connectors between steps — fill in green up to the current step.
    if (this.hasStepConnectorTarget) {
      this.stepConnectorTargets.forEach((connector, i) => {
        connector.classList.remove("bg-blue-600", "bg-green-500", "bg-gray-200", "dark:bg-base-700")
        if (i < index) {
          connector.classList.add("bg-green-500")
        } else {
          connector.classList.add("bg-gray-200", "dark:bg-base-700")
        }
      })
    }

    // Labels — highlight the active step's label, mute the rest.
    if (this.hasStepLabelTarget) {
      this.stepLabelTargets.forEach((labelEl, i) => {
        labelEl.classList.remove("text-gray-900", "dark:text-white", "text-gray-500", "dark:text-base-400", "font-semibold")
        if (i === index) {
          labelEl.classList.add("text-gray-900", "dark:text-white", "font-semibold")
        } else {
          labelEl.classList.add("text-gray-500", "dark:text-base-400")
        }
      })
    }

    // Mobile-only caption: current step label + number
    if (this.hasCurrentStepLabelTarget && this.hasStepLabelTarget) {
      const activeLabel = this.stepLabelTargets[index]?.textContent?.trim()
      if (activeLabel) this.currentStepLabelTarget.textContent = activeLabel
    }
    if (this.hasCurrentStepNumberTarget) {
      this.currentStepNumberTarget.textContent = index + 1
    }
  }

  /**
   * Hook called during step transitions. Override in subclass for
   * POS-specific transitions (e.g., payment sync, PMS data).
   */
  onStepTransition(fromStep, toStep) {
    if (fromStep === 0 && toStep === 1) {
      this._syncPrimaryDiverToDives()
    }
    if (toStep === 2) {
      this.buildReview()
    }
    if (toStep === 3) {
      this.onEnterPaymentStep()
    }
  }

  /**
   * Hook called when entering the payment step. Override in subclass
   * for payment controller sync, surcharge setup, etc.
   */
  onEnterPaymentStep() {
    const divesController = this._getDivesController()
    if (!divesController) return

    const totalCents = divesController.getGrandTotal()

    if (this.hasPaymentTotalTarget) {
      this.paymentTotalTarget.textContent = formatMoney(totalCents)
    }
  }

  // ===== Guest Selection =====

  selectDiver(event) {
    event.preventDefault()
    const button = event.currentTarget
    const diverId = button.dataset.diverId
    const diverName = button.dataset.diverName
    const diverEmail = button.dataset.diverEmail
    const diverPhone = button.dataset.diverPhone

    this.setGuestFields(diverId, diverName, diverEmail, diverPhone)

    // Visual feedback on selected
    this.element.querySelectorAll("[data-diver-id]").forEach(btn => {
      btn.classList.remove("ring-2", "ring-blue-500", "dark:ring-blue-400", "bg-blue-50", "dark:bg-blue-900/30")
    })
    button.classList.add("ring-2", "ring-blue-500", "dark:ring-blue-400", "bg-blue-50", "dark:bg-blue-900/30")
  }

  setGuestFields(diverId, name, email, phone) {
    if (this.hasDiverIdFieldTarget && diverId) {
      this.diverIdFieldTarget.value = diverId
    }

    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    const emailField = this.element.querySelector('[name="booking[email]"]')
    const phoneField = this.element.querySelector('[name="booking[phone]"]')

    if (nameField) nameField.value = name || ""
    if (emailField) emailField.value = email || ""
    if (phoneField) phoneField.value = phone || ""

    if (this.hasDiverNameDisplayTarget) {
      this.diverNameDisplayTarget.textContent = name
      this.diverNameDisplayTarget.classList.remove("hidden")
    }

    this.clearStepError(this.stepTargets[0])
  }

  // ===== Validation =====

  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue]
    if (!currentStep) return true

    switch (this.currentStepValue) {
      case 0: // Guest step
        return this.validateGuestStep(currentStep)
      case 1: // Dives step
        return this.validateDivesStep(currentStep)
      case 2: // Review step — always valid
        return true
      case 3: // Payment step — validated at submit
        return true
      default:
        return true
    }
  }

  validateGuestStep(step) {
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    if (!nameField || !nameField.value.trim()) {
      this.showStepError(step, "Please select or register a guest before continuing.")
      return false
    }
    this.clearStepError(step)
    return true
  }

  validateDivesStep(step) {
    const divesController = this._getDivesController()
    if (!divesController) {
      this.showStepError(step, "Dive selection controller not found.")
      return false
    }

    const summary = divesController.getSelectionSummary()
    const hasItems = summary.some(group => group.items.length > 0)

    if (!hasItems) {
      this.showStepError(step, "Please select at least one dive package for a diver.")
      return false
    }

    this.clearStepError(step)
    return true
  }

  showStepError(step, message) {
    let errorDiv = step.querySelector(".step-error")
    if (!errorDiv) {
      errorDiv = document.createElement("div")
      errorDiv.className = "step-error bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4"
      step.insertBefore(errorDiv, step.firstChild)
    }
    errorDiv.textContent = message
  }

  clearStepError(step) {
    const errorDiv = step.querySelector(".step-error")
    if (errorDiv) errorDiv.remove()
  }

  // ===== Primary Diver Sync =====

  _syncPrimaryDiverToDives() {
    const divesController = this._getDivesController()
    if (!divesController) return

    const diverId = this.hasDiverIdFieldTarget ? this.diverIdFieldTarget.value : null
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    const name = nameField?.value || "Diver 1 Full Name"

    divesController.setPrimaryDiver(diverId, name)
  }

  // ===== Review =====

  buildReview() {
    this.buildReviewCards()
    this.buildReviewItems()
  }

  buildReviewCards() {
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    const emailField = this.element.querySelector('[name="booking[email]"]')
    const diverId = this.hasDiverIdFieldTarget ? this.diverIdFieldTarget.value : ""

    // Dive dates
    const dateInputs = this.element.querySelectorAll('input[name="booking[dive_dates][]"]')
    const uniqueDateValues = [...new Set(Array.from(dateInputs).map(i => i.value).filter(Boolean))].sort()
    const formattedDates = uniqueDateValues.map(v => {
      const d = new Date(v + "T00:00:00")
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    }).join(", ")

    // Diver count
    const divesController = this._getDivesController()
    const diverCount = divesController ? divesController.getDiverCount() : 1

    if (this.hasReviewCardsTarget) {
      this.reviewCardsTarget.innerHTML = `
        <div class="flex items-center gap-4 p-4 border border-gray-200 dark:border-base-700 rounded-xl bg-white dark:bg-base-800">
          <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
          </div>
          <div class="min-w-0">
            <div class="text-xs font-semibold text-blue-500 dark:text-blue-300 uppercase tracking-wider">Primary Guest</div>
            <div class="font-bold text-gray-900 dark:text-white truncate">${nameField?.value || "N/A"}</div>
            <div class="text-xs text-gray-500 dark:text-base-400 truncate">${emailField?.value || ""}${diverId ? " \u2022 ID: " + diverId : ""}</div>
          </div>
        </div>
        <div class="flex items-center gap-4 p-4 border border-gray-200 dark:border-base-700 rounded-xl bg-white dark:bg-base-800">
          <div class="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
          </div>
          <div>
            <div class="text-xs font-semibold text-blue-500 dark:text-blue-300 uppercase tracking-wider">Divers</div>
            <div class="font-bold text-gray-900 dark:text-white">${diverCount} Diver${diverCount !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <div class="flex items-center gap-4 p-4 border border-gray-200 dark:border-base-700 rounded-xl bg-white dark:bg-base-800">
          <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div>
            <div class="text-xs font-semibold text-blue-500 dark:text-blue-300 uppercase tracking-wider">Dive Dates</div>
            <div class="font-bold text-gray-900 dark:text-white">${formattedDates || "None"}</div>
          </div>
        </div>`
    }
  }

  buildReviewItems() {
    const divesController = this._getDivesController()
    const summary = divesController?.getSelectionSummary?.() || []

    let grandTotal = 0
    let rowsHtml = ""

    summary.forEach(diverGroup => {
      // Diver group header
      rowsHtml += `
        <div class="px-6 py-2.5 bg-gray-50 dark:bg-base-900 border-b border-gray-100 dark:border-base-700">
          <span class="text-xs font-bold text-blue-500 dark:text-blue-300 uppercase tracking-wider">${this._escapeHtml(diverGroup.label)}</span>
          ${diverGroup.dates ? `<span class="text-xs text-gray-400 dark:text-base-300 ml-2">${diverGroup.dates}</span>` : ""}
        </div>`

      diverGroup.items.forEach(item => {
        const lineTotal = item.qty * item.price_cents
        grandTotal += lineTotal
        const isAddon = item.isAddon || this.constructor.ADDON_CATEGORIES.includes(item.category)
        const icon = isAddon ? this.constructor.ADDON_ICON : this.constructor.PACKAGE_ICON

        rowsHtml += `
          <div class="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 items-center">
            <div class="sm:col-span-6">
              <div class="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1.5">${icon} ${this._escapeHtml(item.label)}</div>
              ${item.dateLabel ? `<div class="text-xs text-gray-400 dark:text-base-300 mt-0.5">${item.dateLabel}</div>` : ""}
            </div>
            <div class="sm:col-span-3 text-center">
              <span class="text-sm font-medium text-gray-900 dark:text-white">${item.qty}</span>
            </div>
            <div class="sm:col-span-3 sm:text-right">
              <span class="text-gray-400 dark:text-base-300 text-sm mr-1">$</span>
              <span class="text-sm font-semibold text-gray-900 dark:text-white">${centsToDecimal(item.price_cents)}</span>
            </div>
          </div>`
      })
    })

    // Hook for subclasses to fold extra line items (e.g. the public wizard's
    // accommodation lines, owned by public_accommodation_controller) into
    // both the review rows and the running grand total. Default no-op keeps
    // POS / single-controller wizards unchanged.
    const extra = this._buildExtraReviewItems()
    if (extra && extra.html) rowsHtml += extra.html
    if (extra && extra.totalCents) grandTotal += extra.totalCents

    if (!rowsHtml) {
      rowsHtml = `<p class="text-sm text-gray-400 dark:text-base-500 text-center py-6">No items selected</p>`
    }

    if (this.hasReviewItemsTarget) {
      this.reviewItemsTarget.innerHTML = rowsHtml
    }

    if (this.hasReviewTotalTarget) {
      this.reviewTotalTarget.textContent = formatMoney(grandTotal)
    }
  }

  // Override in subclasses that need to append non-dive items to the Review
  // step total (e.g. public-accommodation lines). Return
  // `{ html: String, totalCents: Number }` or null. Default is no extras.
  _buildExtraReviewItems() {
    return null
  }

  // ===== Form Submission =====

  async submitBooking(event) {
    event.preventDefault()

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Processing..."
    }

    if (!this.hasFormTarget) return

    try {
      const formData = new FormData(this.formTarget)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        this.onSubmitSuccess(html)
      } else {
        const text = await response.text()
        this.onSubmitError(text)
      }
    } catch (error) {
      console.error("Submission failed:", error)
      this.onSubmitNetworkError(error)
    }
  }

  /**
   * Submit the form directly (standard browser submission).
   * Used for edit mode where we skip payment and let Rails redirect.
   */
  submitForm(event) {
    event.preventDefault()
    if (!this.hasFormTarget) return
    this.formTarget.submit()
  }

  /**
   * Hook called on successful form submission. Override in subclass
   * for custom success behavior.
   */
  onSubmitSuccess(html) {
    // Inject success content into the Done step (last step) and show it
    const doneStep = this.stepTargets[this.totalStepsValue - 1]
    if (doneStep) {
      doneStep.innerHTML = html
    }
    this.showStep(this.totalStepsValue - 1)
  }

  /**
   * Hook called on form submission error. Override in subclass
   * for custom error behavior.
   */
  onSubmitError(responseText) {
    const errorMatch = responseText.match(/alert[^>]*>([^<]+)</) || responseText.match(/"error":"([^"]+)"/)
    const message = errorMatch ? errorMatch[1] : "Submission failed. Please try again."
    this.showStepError(this.stepTargets[3], message)
    this._resetSubmitButton()
  }

  /**
   * Hook called on network error during submission.
   */
  onSubmitNetworkError(_error) {
    this.showStepError(this.stepTargets[3], "Network error. Please try again.")
    this._resetSubmitButton()
  }

  _resetSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Submit"
    }
  }

  // ===== Private Helpers =====

  _detectDivesIdentifier() {
    // Try common identifiers
    const candidates = ["pos-dives", "public-dives"]
    for (const id of candidates) {
      const el = this.element.querySelector(`[data-controller*="${id}"]`)
      if (el) return id
    }
    return null
  }

  _getDivesElement() {
    const id = this.divesControllerIdentifier
    if (!id) return null
    return this.element.querySelector(`[data-controller*="${id}"]`)
  }

  _getDivesController(el) {
    if (!el) el = this._getDivesElement()
    if (!el) return null
    const id = this.divesControllerIdentifier
    return this.application.getControllerForElementAndIdentifier(el, id)
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML
  }
}
